import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createGame } from '../../lib/bettingApi';
import { supabase } from '../../lib/supabase';
import ImageCropper from './ImageCropper';
import './CreateGame.css';

function CreateGame({ onGameCreated, onClose, onLoginRequired }) {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [titleImage, setTitleImage] = useState('');
  const [titleImageFile, setTitleImageFile] = useState(null);
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('1d'); // Default to 1 day
  
  // Teams array with default colors
  const defaultColors = ['#2551AF', '#E23838', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899'];
  // Start with exactly 2 teams; additional teams can be added via the "+ Add Team" button
  const [teams, setTeams] = useState([
    { id: 1, name: '', image: '', imageFile: null, color: '#2551AF' },
    { id: 2, name: '', image: '', imageFile: null, color: '#E23838' }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Image cropping state
  const [showCropper, setShowCropper] = useState(false);
  const [croppingFor, setCroppingFor] = useState(null); // team ID
  const [imageToCrop, setImageToCrop] = useState(null);
  
  const fileInputRefs = useRef({});
  const titleImageInputRef = useRef(null);
  
  // Helper to get file input ref for a team
  const getFileInputRef = (teamId) => {
    if (!fileInputRefs.current[teamId]) {
      fileInputRefs.current[teamId] = React.createRef();
    }
    return fileInputRefs.current[teamId];
  };
  
  // Add a new team (max 7 teams)
  const addTeam = () => {
    if (teams.length >= 7) return;
    const nextId = Math.max(...teams.map(t => t.id), 0) + 1;
    const colorIndex = (nextId - 1) % defaultColors.length;
    setTeams([...teams, { 
      id: nextId, 
      name: '', 
      image: '', 
      imageFile: null, 
      color: defaultColors[colorIndex] 
    }]);
  };
  
  // Remove a team (minimum 2 teams required)
  const removeTeam = (teamId) => {
    if (teams.length <= 2) return; // Prevent deletion if only 2 teams remain
    setTeams(prevTeams => {
      const teamToRemove = prevTeams.find(t => t.id === teamId);
      if (teamToRemove && teamToRemove.image && teamToRemove.image.startsWith('blob:')) {
        URL.revokeObjectURL(teamToRemove.image);
      }
      return prevTeams.filter(t => t.id !== teamId);
    });
  };
  
  // Update team field
  const updateTeam = (teamId, field, value) => {
    setTeams(prevTeams => prevTeams.map(team => 
      team.id === teamId ? { ...team, [field]: value } : team
    ));
  };

  // Handle file selection
  const handleFileSelect = (teamId, file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      setImageToCrop(imageUrl);
      setCroppingFor(teamId);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  // Handle title image selection (no cropping, simple square preview)
  const handleTitleImageSelect = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }
    // Revoke previous object URL if any
    if (titleImage && titleImage.startsWith('blob:')) {
      URL.revokeObjectURL(titleImage);
    }
    const objectUrl = URL.createObjectURL(file);
    setTitleImage(objectUrl);
    setTitleImageFile(file);
  };

  // Handle cropped image
  const handleCropComplete = async (croppedImageUrl) => {
    if (croppingFor) {
      const teamId = croppingFor;
      setTeams(prevTeams => prevTeams.map(team => {
        if (team.id === teamId) {
          // Revoke old blob URL if exists
          if (team.image && team.image.startsWith('blob:')) {
            URL.revokeObjectURL(team.image);
          }
          return { ...team, image: croppedImageUrl };
        }
        return team;
      }));
      
      // Convert blob URL to File if needed
      try {
        const response = await fetch(croppedImageUrl);
        const blob = await response.blob();
        const file = new File([blob], `team-${teamId}.jpg`, { type: 'image/jpeg' });
        setTeams(prevTeams => prevTeams.map(team => 
          team.id === teamId ? { ...team, imageFile: file } : team
        ));
      } catch (error) {
        console.error('Error converting cropped image to file:', error);
      }
    }
    setShowCropper(false);
    setCroppingFor(null);
    setImageToCrop(null);
  };

  // Upload image to Supabase Storage
  const uploadImageToStorage = async (file, teamSide) => {
    if (!file || !user) return null;

    try {
      const fileExt = file.name.split('.').pop() || 'jpg';
      // Store in user's folder: {user_id}/{timestamp}-{side}.{ext}
      // Note: Don't include 'team-images/' in path - .from() already specifies the bucket
      const fileName = `${Date.now()}-${teamSide}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('team-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Storage upload error:', error);
        console.error('Error details:', {
          message: error.message,
          statusCode: error.statusCode,
          error: error.error
        });
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('team-images')
        .getPublicUrl(filePath);

      console.log('Image uploaded successfully:', publicUrl);
      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(false);

    // Check if user is logged in - MUST be first check before any validation
    if (!user) {
      // Clear any error messages
      setError('');
      
      // Verify onLoginRequired is provided and is a function
      if (onLoginRequired && typeof onLoginRequired === 'function') {
        // Call onLoginRequired to show signup window
        // Keep the create game modal open so user can continue after signing up
        onLoginRequired();
      } else {
        // Fallback: show error only if onLoginRequired is not provided
        setError('Please log in to create votes');
      }
      return;
    }

    setLoading(true);

    if (!title.trim()) {
      setError('Title is required');
      setLoading(false);
      return;
    }

    try {
      // Ensure we have at least 2 teams and at most 7 teams
      if (teams.length < 2) {
        setError('Please add at least 2 teams');
        setLoading(false);
        return;
      }
      
      if (teams.length > 7) {
        setError('Maximum 7 teams allowed');
        setLoading(false);
        return;
      }

      // Upload images to Supabase Storage for all teams if files are selected
      const teamsWithImages = await Promise.all(teams.map(async (team, index) => {
        let finalImage = team.image;
        
        if (team.imageFile) {
          try {
            finalImage = await uploadImageToStorage(team.imageFile, `team-${index + 1}`);
          } catch (err) {
            console.error(`Error uploading Team ${index + 1} image:`, err);
            // If upload fails but there's a URL (not blob/data), use that
            if (!team.image || team.image.startsWith('blob:') || team.image.startsWith('data:')) {
              throw new Error(`Failed to upload Team ${index + 1} image. Please try again or use an image URL.`);
            }
          }
        }
        
        return {
          name: team.name.trim(),
          image: finalImage || null,
          color: team.color || null
        };
      }));

      // Upload title image if selected
      let finalTitleImage = titleImage;
      if (titleImageFile) {
        try {
          finalTitleImage = await uploadImageToStorage(titleImageFile, 'title');
        } catch (err) {
          console.error('Error uploading title image:', err);
          if (!titleImage || titleImage.startsWith('blob:') || titleImage.startsWith('data:')) {
            throw new Error('Failed to upload title image. Please try again or remove the image.');
          }
        }
      }

      // Calculate end date based on selected duration
      const now = new Date();
      let endsAt;
      
      switch (duration) {
        case '1d':
          endsAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 1 day
          break;
        case '7d':
          endsAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days
          break;
        case '30d':
          endsAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days
          break;
        default:
          endsAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // Default to 1 day
      }
      
      const endsAtISO = endsAt.toISOString();

      const gameData = {
        title: title.trim(),
        description: description.trim() || null,
        ends_at: endsAtISO,
        title_image: finalTitleImage || null,
        teams: teamsWithImages
      };

      const result = await createGame(gameData);

      if (result.success) {
        setSuccess(true);
        // Clear all state
        setTitle('');
        // Clear title image
        if (titleImage && titleImage.startsWith('blob:')) {
          URL.revokeObjectURL(titleImage);
        }
        setTitleImage('');
        setTitleImageFile(null);
        setDescription('');
        setDuration('1d'); // Reset to default
        // Reset back to the default of 2 teams
        setTeams([
          { id: 1, name: '', image: '', imageFile: null, color: '#2551AF' },
          { id: 2, name: '', image: '', imageFile: null, color: '#E23838' }
        ]);
        
        // Notify parent component
        if (onGameCreated) {
          onGameCreated(result.game);
        }

        // Close modal after 1 second
        setTimeout(() => {
          if (onClose) onClose();
        }, 1000);
      } else {
        setError(result.error || 'Failed to create vote');
      }
    } catch (err) {
      console.error('Error creating vote:', err);
      setError(err.message || 'Failed to create vote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Clear error when user logs in or when component mounts
  useEffect(() => {
    if (user) {
      setError('');
    }
    // Clear any existing errors when component mounts
    setError('');
  }, [user]);

  // Cleanup blob URLs on unmount
  React.useEffect(() => {
    return () => {
      teams.forEach(team => {
        if (team.image && team.image.startsWith('blob:')) {
          URL.revokeObjectURL(team.image);
        }
      });
      if (titleImage && titleImage.startsWith('blob:')) {
        URL.revokeObjectURL(titleImage);
      }
    };
  }, [teams, titleImage]);

  // Handle cancel/close - cleanup
  const handleClose = () => {
    // Cleanup blob URLs
    teams.forEach(team => {
      if (team.image && team.image.startsWith('blob:')) {
        URL.revokeObjectURL(team.image);
      }
    });
    if (titleImage && titleImage.startsWith('blob:')) {
      URL.revokeObjectURL(titleImage);
    }
    if (onClose) onClose();
  };

  return (
    <div className="create-game-container">
      <div className="create-game-header">
        <h2>Create New Vote</h2>
        {onClose && (
          <button className="close-create-game-btn" onClick={handleClose}>×</button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="create-game-form">
        {/* Vote Title with square image on the left */}
        <div className="form-group">
          <div className="title-row">
            <div className="title-image-preview-container">
              {titleImage ? (
                <img
                  src={titleImage}
                  alt="Title preview"
                  className="title-image-preview"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : null}
              <input
                type="file"
                ref={titleImageInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleTitleImageSelect(e.target.files[0]);
                  }
                }}
              />
              <button
                type="button"
                className="upload-image-btn"
                onClick={() => titleImageInputRef.current?.click()}
                disabled={loading}
              >
                {titleImage ? 'Change Image' : 'Upload Image'}
              </button>
            </div>
            <div className="title-input-container">
              <label htmlFor="vote-title" className="form-label">
                Vote Title *
              </label>
              <input
                id="vote-title"
                type="text"
                className="form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Will it rain tomorrow?"
                disabled={loading}
                required
                maxLength={200}
              />
            </div>
          </div>
        </div>

        {/* Description - Smaller */}
        <div className="form-group description-group">
          <label htmlFor="vote-description" className="form-label">
            Description (optional)
          </label>
          <textarea
            id="vote-description"
            className="form-textarea form-textarea-small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the vote or event..."
            disabled={loading}
            rows={2}
            maxLength={1000}
          />
        </div>

        {/* Duration Selection */}
        <div className="form-group">
          <label className="form-label">Vote Duration *</label>
          <div className="duration-selector">
            <label className="duration-option">
              <input
                type="radio"
                name="duration"
                value="1d"
                checked={duration === '1d'}
                onChange={(e) => setDuration(e.target.value)}
                disabled={loading}
              />
              <span className="duration-label">1 Day</span>
            </label>
            <label className="duration-option">
              <input
                type="radio"
                name="duration"
                value="7d"
                checked={duration === '7d'}
                onChange={(e) => setDuration(e.target.value)}
                disabled={loading}
              />
              <span className="duration-label">7 Days</span>
            </label>
            <label className="duration-option">
              <input
                type="radio"
                name="duration"
                value="30d"
                checked={duration === '30d'}
                onChange={(e) => setDuration(e.target.value)}
                disabled={loading}
              />
              <span className="duration-label">30 Days</span>
            </label>
          </div>
        </div>

        {/* Teams Container - Single Column Layout */}
        <div className="teams-columns-container">
          {teams.map((team, index) => {
            const teamColumnClasses = ['team-column-left', 'team-column-right', 'team-column-three'];
            const columnClass = teamColumnClasses[index % teamColumnClasses.length] || 'team-column-left';
            const fileInputRef = getFileInputRef(team.id);
            
            return (
              <div key={team.id} className={`team-column ${columnClass}`}>
                {teams.length > 2 && (
                  <button
                    type="button"
                    className="close-team-btn"
                    onClick={() => removeTeam(team.id)}
                    disabled={loading}
                    title="Remove team"
                  >
                    ×
                  </button>
                )}
                <div className="team-row-container">
                  <div className="team-image-preview-container">
                    {team.image ? (
                      <img 
                        src={team.image} 
                        alt={`Team ${team.id} preview`} 
                        className="team-image-preview"
                        onError={(e) => { 
                          e.target.style.display = 'none';
                          const placeholder = e.target.nextElementSibling;
                          if (placeholder) placeholder.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    {!team.image && (
                      <div className="team-image-placeholder"></div>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFileSelect(team.id, e.target.files[0]);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="upload-image-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                    >
                      {team.image ? 'Change Image' : 'Upload Image'}
                    </button>
                  </div>
                  <div className="team-fields-container">
                    <div className="form-group">
                      <input
                        id={`team-${team.id}-name`}
                        type="text"
                        className="form-input"
                        value={team.name}
                        onChange={(e) => updateTeam(team.id, 'name', e.target.value)}
                        placeholder={`Name ${index + 1}`}
                        disabled={loading}
                        maxLength={100}
                      />
                    </div>
                    <div className="form-group">
                      <div className="color-picker-container">
                        <div className="color-picker-main">
                          <input
                            id={`team-${team.id}-color`}
                            type="color"
                            value={team.color}
                            onChange={(e) => updateTeam(team.id, 'color', e.target.value)}
                            className="color-input"
                            disabled={loading}
                          />
                          <input
                            type="text"
                            value={team.color}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                                updateTeam(team.id, 'color', value || defaultColors[index % defaultColors.length]);
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              if (!/^#[0-9A-Fa-f]{6}$/.test(value)) {
                                updateTeam(team.id, 'color', defaultColors[index % defaultColors.length]);
                              }
                            }}
                            className="color-hex-input"
                            placeholder={team.color}
                            disabled={loading}
                            maxLength={8}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Add Team Button - Only show if less than 7 teams */}
        {teams.length < 7 && (
          <div className="add-team-container">
            <button
              type="button"
              className="add-team-btn"
              onClick={addTeam}
              disabled={loading}
            >
              + Add Team
            </button>
          </div>
        )}

        {error && <div className="create-game-error">{error}</div>}
        {success && (
          <div className="create-game-success">
            Vote created successfully! It will last for {
              duration === '1d' ? '1 day' : 
              duration === '7d' ? '7 days' : 
              '30 days'
            } and then be automatically processed. Redirecting...
          </div>
        )}

        <div className="create-game-actions">
          {onClose && (
            <button
              type="button"
              className="cancel-btn"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="create-game-btn"
            disabled={loading || !title.trim()}
          >
            {loading ? 'Creating...' : 'Create Vote'}
          </button>
        </div>
      </form>

      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <ImageCropper
          image={imageToCrop}
          onCropComplete={handleCropComplete}
          onClose={() => {
            setShowCropper(false);
            setCroppingFor(null);
            setImageToCrop(null);
          }}
          aspectRatio={1}
        />
      )}
    </div>
  );
}

export default CreateGame;

